"use client";

import { useDonationTimeSerie } from "@/hooks/useDonationTimeSerie";
import type { DonationTimeSerieFilters } from "@/hooks/useDonationTimeSerie";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { currency } from "@/lib/format";
import { Card, CardContent } from "@gogive/design-system";
import { useMemo } from "react";
import Loader from "@/components/Loader";
import {
  addDays,
  addMonths,
  addWeeks,
  startOfDay,
  startOfMonth,
  startOfWeek,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  getDate,
} from "date-fns";

const CHART_ACCENT = "var(--primary)";

type TimeSerieRow = {
  createdAt: string | Date;
  grossAmount?: number | null;
  gogiveAmount?: number | null;
  netAmount?: number | null;
};

function donorPaidCents(row: TimeSerieRow): number {
  const g = row.grossAmount ?? 0;
  const fee = row.gogiveAmount ?? 0;
  return g - fee;
}

function groupDonationsByInterval(
  donations: TimeSerieRow[],
  interval: "day" | "month" | "week",
  from?: Date,
  to?: Date
): { name: string; total: number }[] {
  const getWeekLabel = (date: Date) => {
    const weekStart = startOfWeek(new Date(date), { weekStartsOn: 1 });
    return `${format(date, "MMM")} ${getDate(weekStart)}`;
  };
  const getDayLabel = (date: Date) => format(startOfDay(date), "dd MMM");

  const dates: Date[] = [];
  const donationDates = donations
    .map((donation) => new Date(donation.createdAt))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());
  const donationStartDate = donationDates[0];
  const donationEndDate = donationDates[donationDates.length - 1];
  const startBound = from ?? donationStartDate;
  const endBound = to ?? donationEndDate;

  if (!startBound || !endBound) {
    return [];
  }

  if (interval === "month") {
    const startDate = startBound;
    const endDate = endBound;
    const numMonths = Math.max(
      0,
      differenceInMonths(endOfMonth(endDate), startOfMonth(startDate))
    );

    for (let i = 0; i <= numMonths; i++) {
      dates.push(addMonths(startDate, i));
    }
  } else if (interval === "week") {
    const startDate = startBound;
    const endDate = endBound;
    const numWeeks = Math.max(
      0,
      differenceInWeeks(
        endOfWeek(endDate, { weekStartsOn: 1 }),
        startOfWeek(startDate, { weekStartsOn: 1 })
      )
    );

    for (let i = 0; i <= numWeeks; i++) {
      dates.push(addWeeks(startDate, i));
    }
  } else {
    const startDate = startBound;
    const endDate = endBound;
    const numDays = Math.max(
      0,
      differenceInDays(endOfDay(endDate), startOfDay(startDate))
    );

    for (let i = 0; i <= numDays; i++) {
      dates.push(addDays(startDate, i));
    }
  }

  return dates.map((date) => {
    const dateKey =
      interval === "month"
        ? format(startOfMonth(date), "MMM yyyy")
        : interval === "week"
        ? getWeekLabel(date)
        : getDayLabel(date);

    const donationsForDate = donations.filter((donation) => {
      const donationDateKey =
        interval === "month"
          ? format(startOfMonth(new Date(donation.createdAt)), "MMM yyyy")
          : interval === "week"
          ? getWeekLabel(new Date(donation.createdAt))
          : getDayLabel(new Date(donation.createdAt));
      return donationDateKey === dateKey;
    });

    const total = donationsForDate.reduce(
      (acc, donation) => acc + donorPaidCents(donation) / 100,
      0
    );

    return { name: dateKey, total };
  });
}

type Props = {
  filters?: DonationTimeSerieFilters;
};

export function DonationsChart({ filters = {} }: Props) {
  const { donations, isLoading } = useDonationTimeSerie(filters);
  const from = useMemo(
    () => (filters.startDate ? new Date(filters.startDate) : undefined),
    [filters.startDate]
  );
  const to = useMemo(
    () => (filters.endDate ? new Date(filters.endDate) : undefined),
    [filters.endDate]
  );
  const spanDays = useMemo(() => {
    if (!from || !to) return undefined;
    return differenceInDays(endOfDay(to), startOfDay(from)) + 1;
  }, [from, to]);
  const datasetSpanDays = useMemo(() => {
    if (donations.length < 2) return undefined;
    const dates = donations
      .map((donation) => new Date(donation.createdAt))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    if (dates.length < 2) return undefined;
    return differenceInDays(endOfDay(dates[dates.length - 1]), startOfDay(dates[0])) + 1;
  }, [donations]);
  const interval = useMemo<"day" | "week" | "month">(() => {
    const effectiveSpanDays = spanDays ?? datasetSpanDays;
    if (effectiveSpanDays == null) return "week";
    if (effectiveSpanDays <= 35) return "day";
    if (effectiveSpanDays <= 210) return "week";
    return "month";
  }, [spanDays, datasetSpanDays]);

  const data = useMemo(
    () =>
      groupDonationsByInterval(donations as TimeSerieRow[], interval, from, to),
    [donations, interval, from, to]
  );

  const noData = useMemo(() => {
    return donations.length === 0 && !isLoading;
  }, [donations.length, isLoading]);

  const chartData = data;
  const showLoader = donations.length === 0 && isLoading;

  return (
    <Card
      id="donations-chart"
      className="relative shrink-0 gap-0 overflow-hidden border-border/60 bg-card pt-8 shadow-sm"
    >
      <CardContent className="px-2 pb-3 sm:px-4">
        <div className="relative h-[220px] w-full sm:h-[240px]">
          <ResponsiveContainer
            width="100%"
            height="100%"
            minHeight={220}
            debounce={150}
          >
            <AreaChart
              data={chartData}
              margin={{ top: 6, right: 6, left: 0, bottom: 4 }}
            >
              <defs>
                <linearGradient
                  id="donationsPageChartFill"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={CHART_ACCENT}
                    stopOpacity={0.2}
                  />
                  <stop
                    offset="100%"
                    stopColor={CHART_ACCENT}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke="#f1f5f9"
                vertical={false}
                className="dark:stroke-border/50"
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10, fontWeight: 600 }}
                tickLine={false}
                axisLine={false}
                className="fill-muted-foreground"
                interval="preserveStartEnd"
                angle={interval === "month" ? 0 : -22}
                textAnchor={interval === "month" ? "middle" : "end"}
                height={interval === "month" ? 28 : 44}
              />
              <YAxis
                tick={{ fontSize: 10, fontWeight: 600 }}
                tickLine={false}
                axisLine={false}
                className="fill-muted-foreground"
                tickFormatter={(value) => currency(value)}
                width={52}
              />
              <Tooltip
                animationDuration={120}
                cursor={{
                  stroke: CHART_ACCENT,
                  strokeWidth: 1,
                  strokeOpacity: 0.35,
                }}
                formatter={(value: number) => currency(value)}
                labelFormatter={(label) => `Period: ${label}`}
                contentStyle={{
                  borderRadius: "0.5rem",
                  border: "1px solid hsl(var(--border))",
                  backgroundColor: "hsl(var(--card))",
                }}
              />
              <Area
                type="monotone"
                dataKey="total"
                stroke={CHART_ACCENT}
                strokeWidth={2}
                fill="url(#donationsPageChartFill)"
                fillOpacity={1}
                dot={false}
                activeDot={{ r: 5 }}
                isAnimationActive
              />
            </AreaChart>
          </ResponsiveContainer>

          {showLoader && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader size={28} />
            </div>
          )}

          {noData && !showLoader && (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="rounded-md border bg-card px-4 py-3 text-sm text-muted-foreground shadow-sm">
                No donations in this range
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
